/**
 * El permiso de Business Central del simulador (ADR-15): una SEGUNDA app
 * de MSAL, distinta de la del Studio (`auth/msal.ts`), porque es otra app
 * de Entra (`appreg-skein-simulador`, infra/naming.md) y otra audiencia.
 *
 * - Autoridad `organizations`: multi-organización. La organización de demo
 *   donde vive CRONUS NO es el tenant de Skein; la cuenta que se conecta es
 *   la de esa organización.
 * - Caché en MEMORIA (`memoryStorage`), la de tokens Y la temporal: nada se
 *   persiste. La temporal (PKCE, `request.params`, `origin.uri` del
 *   intercambio de la ventana) va por defecto a sessionStorage aunque la de
 *   tokens esté en memoria, así que se fija a mano. Cerrar la pestaña
 *   olvida la sesión; el token, además, solo vive en el estado de React y
 *   viaja como `tokenFuente` en cada llamada (la API no lo guarda).
 * - Ventana emergente, no redirección: redirigir recargaría la SPA y con
 *   ella se perdería todo lo que la pantalla tiene en memoria. La ventana
 *   aterriza en `simulador-popup.html`, una página en blanco: si aterrizara
 *   en la SPA, el MSAL del Studio arrancaría dentro y podría comerse la
 *   respuesta que esta app espera leer.
 * - La app se crea al primer uso y solo si hay client id: sin
 *   `VITE_SIMULADOR_CLIENT_ID` la pantalla no rompe, dice qué falta.
 */
import { BrowserAuthError, PublicClientApplication, type AccountInfo, type AuthenticationResult, type IPublicClientApplication } from "@azure/msal-browser";
import { ENV } from "../../env";
import { ConexionCancelada, type CuentaFuente, type SesionFuente } from "./cuenta";
import { faltaConfigurar } from "./logica";

/**
 * Permiso delegado de Business Central (API v2.0 y API del centro de
 * administración, que lista los entornos). POR VERIFICAR CONTRA LA APP
 * REAL: es el permiso delegado `user_impersonation` de la API «Dynamics 365
 * Business Central»; la documentación de Microsoft no se pudo consultar al
 * escribir esto. Si la app de Entra pide `Financials.ReadWrite.All`, se
 * cambia aquí. La escritura no se usa: el adaptador manda
 * `Data-Access-Intent: ReadOnly` y solo hace GET.
 */
export const BC_SCOPE = "https://api.businesscentral.dynamics.com/user_impersonation";
export const AUTHORITY = "https://login.microsoftonline.com/organizations";
const REDIRECT = "/simulador-popup.html";

let app: Promise<IPublicClientApplication> | undefined;
function pca(): Promise<IPublicClientApplication> {
  app ??= PublicClientApplication.createPublicClientApplication({
    auth: {
      clientId: ENV.simuladorClientId,
      authority: AUTHORITY,
      redirectUri: `${window.location.origin}${REDIRECT}`,
      postLogoutRedirectUri: `${window.location.origin}${REDIRECT}`,
    },
    cache: { cacheLocation: "memoryStorage", temporaryCacheLocation: "memoryStorage" },
  });
  return app;
}

const request = { scopes: [BC_SCOPE] };
let cuenta: AccountInfo | null = null;

const sesion = (r: AuthenticationResult): SesionFuente => {
  cuenta = r.account;
  return { token: r.accessToken, cuenta: r.account.name ?? r.account.username };
};
/** Cerrar la ventana sin aceptar no es un fallo: la pantalla vuelve a
    «desconectado» sin rojo. Una ventana BLOQUEADA por el navegador sí lo es
    (`popup_window_error`): eso se cuenta, porque hay que permitirla. */
const cancelada = (err: unknown): never => {
  if (err instanceof BrowserAuthError && err.errorCode === "user_cancelled") throw new ConexionCancelada();
  throw err;
};

export const cuentaSimulador: CuentaFuente = {
  configurada: !faltaConfigurar(ENV.simuladorClientId),
  async conectar() {
    const msal = await pca();
    // select_account: la cuenta de la organización de demo, aunque el
    // navegador ya tenga abierta la de Skein
    const r = await msal.loginPopup({ ...request, prompt: "select_account" }).catch(cancelada);
    if (r.accessToken) return sesion(r);
    return sesion(await msal.acquireTokenSilent({ ...request, account: r.account }));
  },
  async renovar() {
    const msal = await pca();
    if (cuenta) {
      try {
        return sesion(await msal.acquireTokenSilent({ ...request, account: cuenta, forceRefresh: true }));
      } catch {
        /* sin sesión renovable: se pide en la ventana */
      }
    }
    return sesion(await msal.acquireTokenPopup({ ...request, prompt: "select_account" }).catch(cancelada));
  },
  async olvidar() {
    // olvidar en local, no cerrar la sesión de Microsoft entera: la cuenta
    // de demo puede estar abierta para otras cosas en el navegador
    const msal = await pca();
    await msal.clearCache(cuenta ? { account: cuenta } : undefined);
    cuenta = null;
  },
};
